import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  X, ChevronLeft, ChevronRight, CheckCircle, XCircle,
  Dumbbell, AlertTriangle, Loader2, Move, Eye, EyeOff, Play, Pause
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatMuscle, formatMovementPattern, formatMovementPatternForExercise, formatDifficulty, isRawValue, cleanExerciseName } from '@/lib/textHygiene';
import ExercisePreferenceButtons from '@/components/ExercisePreferenceButtons';

import { useSlowConnection } from '@/components/SmartVideo';

// Large movement arrow SVG component - positioned beside body parts
function MovementArrow({ direction, size = 48, delay = 0 }) {
  const getPath = () => {
    switch (direction) {
      case 'up':
        return 'M24 40 L24 8 M12 20 L24 8 L36 20';
      case 'down':
        return 'M24 8 L24 40 M12 28 L24 40 L36 28';
      case 'left':
        return 'M40 24 L8 24 M20 12 L8 24 L20 36';
      case 'right':
        return 'M8 24 L40 24 M28 12 L40 24 L28 36';
      case 'up-right':
        return 'M12 36 L36 12 M20 12 L36 12 L36 28';
      case 'down-left':
        return 'M36 12 L12 36 M12 20 L12 36 L28 36';
      case 'up-left':
        return 'M36 36 L12 12 M12 28 L12 12 L28 12';
      case 'down-right':
        return 'M12 12 L36 36 M36 20 L36 36 L20 36';
      case 'rotate-cw':
        return 'M24 8 A16 16 0 1 1 8 24 M8 16 L8 24 L16 24';
      case 'rotate-ccw':
        return 'M24 8 A16 16 0 1 0 40 24 M40 16 L40 24 L32 24';
      default:
        return 'M24 40 L24 8 M12 20 L24 8 L36 20';
    }
  };

  return (
    <motion.svg
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: delay * 0.15, type: 'spring', stiffness: 200 }}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className="drop-shadow-lg"
    >
      <path
        d={getPath()}
        fill="none"
        stroke="var(--accent-hex)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );
}

// Movement Guide Panel - shows arrows with labels clearly separated
function MovementGuidePanel({ arrows, isVisible }) {
  if (!arrows || arrows.length === 0 || !isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="bg-card/95 backdrop-blur-sm border border-border rounded-xl p-4 mt-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Move size={16} className="text-accent" />
        <h4 className="font-heading font-semibold text-sm">Movement Guide</h4>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {arrows.map((arrow, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.1 }}
            className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
          >
            <div className="shrink-0 w-10 h-10 rounded-lg bg-background/80 flex items-center justify-center">
              <MovementArrow direction={arrow.direction} size={32} delay={i} />
            </div>
            <span className="text-xs text-foreground font-medium leading-tight">
              {arrow.label}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// Clean image display - mobile responsive, no arrows overlay
// PREMIUM STYLING: Thick neon border
function ExerciseImageWithArrows({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div className="relative p-1">
      {/* THICK NEON BORDER */}
      <div 
        className="relative rounded-2xl overflow-hidden"
        style={{
          padding: '3px',
          background: 'linear-gradient(135deg, var(--accent-hex) 0%, var(--accent-hover-hex) 50%, var(--accent-hex) 100%)',
          boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.3)',
        }}
      >
        <div className="bg-gradient-to-b from-zinc-900 to-black rounded-xl overflow-hidden">
          {/* Mobile-optimized image container */}
          <div className="relative w-full aspect-[4/3] sm:aspect-[4/3] overflow-hidden flex items-center justify-center">
            {/* Loading state */}
            {!loaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 animate-pulse" />
            )}
            
            <img
              src={src}
              alt={alt}
              className={`w-full h-full object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              data-testid="exercise-detail-image"
              onLoad={() => setLoaded(true)}
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Video player component - optimized for mobile performance
// PREMIUM STYLING: Thick neon border, simple play button
function ExerciseVideoPlayer({ src, poster }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [useIframe, setUseIframe] = useState(false);
  const isSlow = useSlowConnection();
  const [userOptedIn, setUserOptedIn] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => {
          console.error('Video play error:', err);
          setHasError(true);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleError = (e) => {
    console.error('Video load error:', e);
    setHasError(true);
  };

  // If video fails, show static fallback
  if (hasError) {
    return (
      <div className="relative p-1">
        <div className="relative rounded-2xl overflow-hidden bg-[#1F1F1F] aspect-video flex items-center justify-center">
          <a href={src} target="_blank" rel="noopener noreferrer" className="text-accent font-heading font-bold text-sm uppercase">
            Open Video ↗
          </a>
        </div>
      </div>
    );
  }

  // Slow connection: show poster with load button instead of video
  if (isSlow && !userOptedIn) {
    return (
      <div className="relative p-1">
        <div className="relative rounded-2xl overflow-hidden" style={{ padding: '3px', background: 'linear-gradient(135deg, var(--accent-hex) 0%, var(--accent-hover-hex) 50%, var(--accent-hex) 100%)', boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.3)' }}>
          <div className="bg-black rounded-2xl overflow-hidden border-2 border-white/15">
            <div className="relative w-full aspect-[4/3] max-h-[42vh] overflow-hidden flex items-center justify-center">
              {poster && <img src={poster} alt="" className="w-full h-full object-contain" />}
              <button onClick={() => setUserOptedIn(true)} className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid="detail-load-video-btn">
                <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center"><Play size={28} className="text-white ml-1" fill="white" /></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-1">
      {/* THICK NEON BORDER */}
      <div 
        className="relative rounded-2xl overflow-hidden"
        style={{
          padding: '3px',
          background: 'linear-gradient(135deg, var(--accent-hex) 0%, var(--accent-hover-hex) 50%, var(--accent-hex) 100%)',
          boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.3)',
        }}
      >
        <div className="bg-black rounded-2xl overflow-hidden border-2 border-white/15">
          <div className="relative w-full aspect-[4/3] max-h-[42vh] overflow-hidden flex items-center justify-center">
            {/* Poster shown while video loads */}
            {poster && !isLoaded && (
              <img src={poster} alt="" className="absolute inset-0 w-full h-full object-contain z-0" />
            )}
            
            <video
              ref={videoRef}
              src={src}
              poster={poster}
              className="w-full h-full object-contain"
              style={{ zIndex: 1 }}
              onLoadedData={(e) => { setIsLoaded(true); e.target.play().catch(() => {}); }}
              onEnded={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={handleError}
              autoPlay
              playsInline
              loop
              muted
              preload="auto"
              data-testid="exercise-video"
            />
            
            {/* Play/pause button */}
            <button
              onClick={togglePlay}
              className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-accent flex items-center justify-center hover:scale-110 transition-transform z-20"
              data-testid="video-play-btn"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-black" fill="currentColor" />
              ) : (
                <Play className="w-4 h-4 text-black ml-0.5" fill="currentColor" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExerciseDetailModal({ 
  exerciseName, 
  onClose,
  // Optional workout context for replacement functionality
  workoutPlanId,
  dayIndex,
  exerciseIndex,
  onExerciseReplaced
}) {
  const { api, user } = useAuth();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageIndex, setImageIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(true);
  const [showGuide, setShowGuide] = useState(true);

  const gender = user?.profile?.gender || 'male';

  useEffect(() => {
    if (exerciseName) {
      fetchExercise();
    }
  }, [exerciseName]);

  const fetchExercise = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/exercises/by-name/${encodeURIComponent(exerciseName)}`);
      setExercise(res.data.exercise);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!exerciseName) return null;

  // Get exercise images - prefer unified 'images' field, fallback to gender-specific
  const getImages = () => {
    if (!exercise) return [];
    
    // First check for unified demo images (new format)
    if (exercise.images && exercise.images.length > 0) {
      return exercise.images;
    }
    
    // Fallback to gender-specific images (legacy format)
    return gender === 'female'
      ? (exercise.images_female || [])
      : (exercise.images_male || []);
  };

  const images = getImages();
  const hasImages = images.length > 0;
  const hasArrows = exercise?.movement_arrows && exercise.movement_arrows.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="exercise-detail-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-[#141414] border rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto pb-32"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          onClick={e => e.stopPropagation()}
          data-testid="exercise-detail-modal"
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-accent" />
            </div>
          ) : !exercise ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">Exercise details not found</p>
              <Button variant="ghost" onClick={onClose} className="mt-4 rounded-full">Close</Button>
            </div>
          ) : (
            <>
              {/* Header — compact sticky bar */}
              <div className="sticky top-0 z-20 backdrop-blur-xl border-b px-4 py-2.5" style={{ background: 'rgba(20,20,20,0.95)', borderColor: 'rgba(255,255,255,0.05)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)' }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-heading font-black uppercase tracking-tight text-white truncate" data-testid="exercise-detail-name">
                      {cleanExerciseName(exercise.name)}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {!isRawValue(exercise.primary_muscle) && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-accent/10 text-accent">
                          {formatMuscle(exercise.primary_muscle)}
                        </span>
                      )}
                      {!isRawValue(exercise.movement_pattern) && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#1F1F1F] text-[#A3A3A3]">
                          {formatMovementPatternForExercise(exercise)}
                        </span>
                      )}
                      {!isRawValue(exercise.difficulty) && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#1F1F1F] text-[#A3A3A3]">
                          {formatDifficulty(exercise.difficulty)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="rounded-full shrink-0 w-8 h-8 text-[#A3A3A3] hover:text-white" onClick={onClose} data-testid="exercise-detail-close">
                    <X size={18} />
                  </Button>
                </div>
              </div>

              {/* Main content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Left: Video (priority) or Image */}
                <div className="relative">
                  {/* If video exists, ONLY show video - no toggle */}
                  {(exercise.video_url || exercise.media_urls?.video) ? (
                    <ExerciseVideoPlayer
                      src={exercise.video_url || exercise.media_urls?.video}
                      poster={hasImages ? images[0] : null}
                    />
                  ) : hasImages ? (
                    <>
                      <ExerciseImageWithArrows
                        src={images[imageIndex]}
                        alt={`${exercise.name} demonstration`}
                      />
                      
                      {/* BodyMax Watermark for Images - gradient style */}
                      <div className="absolute bottom-4 right-4 pointer-events-none z-10">
                        <span 
                          className="font-bold text-base tracking-wide"
                          style={{ 
                            background: 'linear-gradient(135deg, var(--accent-hex) 0%, #ffffff 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: '0 2px 10px rgba(var(--accent-rgb), 0.3)',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                        >
                          BodyMax
                        </span>
                      </div>
                      
                      {/* Image controls - only show if multiple images */}
                      {images.length > 1 && (
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center">
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="secondary"
                              className="h-8 w-8 rounded-full bg-background/60 text-foreground hover:bg-background/80 border-0"
                              onClick={() => setImageIndex(i => i > 0 ? i - 1 : images.length - 1)}
                            >
                              <ChevronLeft size={16} />
                            </Button>
                            <span className="text-xs text-foreground font-mono bg-background/60 px-2 py-1 rounded-full">
                              {imageIndex + 1}/{images.length}
                            </span>
                            <Button
                              size="icon"
                              variant="secondary"
                              className="h-8 w-8 rounded-full bg-background/60 text-foreground hover:bg-background/80 border-0"
                              onClick={() => setImageIndex(i => i < images.length - 1 ? i + 1 : 0)}
                            >
                              <ChevronRight size={16} />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Gender indicator - only show for legacy images */}
                      {!exercise.images?.length && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-background/60 text-foreground border-0 rounded-full text-xs capitalize">
                            {gender} view
                          </Badge>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="aspect-[3/4] bg-muted flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <Dumbbell size={40} className="mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Images coming soon</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Instructions and cues */}
                <div className="p-5 space-y-5 lg:border-l lg:max-h-[70vh] lg:overflow-y-auto" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  {/* Description */}
                  {exercise.description && (
                    <div>
                      <p className="text-base text-white/70 leading-relaxed" data-testid="exercise-description">
                        {exercise.description}
                      </p>
                    </div>
                  )}

                  {/* Movement Guide - Clear visual explanation */}
                  {hasArrows && (
                    <div className="p-4 rounded-xl bg-[#1F1F1F] border border-white/5">
                      <button 
                        onClick={() => setShowGuide(!showGuide)}
                        className="w-full flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Move size={18} className="text-accent" />
                          <h3 className="font-heading font-semibold text-sm">Movement Cues</h3>
                        </div>
                        <ChevronRight 
                          size={18} 
                          className={`text-muted-foreground transition-transform ${showGuide ? 'rotate-90' : ''}`} 
                        />
                      </button>
                      
                      <AnimatePresence>
                        {showGuide && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-3 space-y-2">
                              {exercise.movement_arrows.map((arrow, i) => (
                                <div key={i} className="flex items-center gap-3">
                                  <div className="shrink-0 w-8 h-8 rounded-lg bg-background flex items-center justify-center">
                                    <MovementArrow direction={arrow.direction} size={24} delay={0} />
                                  </div>
                                  <span className="text-sm text-foreground">{arrow.label}</span>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Muscles worked */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm text-white/50 font-heading uppercase tracking-wider">Targets</span>
                    {!isRawValue(exercise.primary_muscle) && (
                      <Badge variant="outline" className="rounded-lg text-sm capitalize px-3 py-1 font-heading font-bold border-accent/30 text-accent">
                        {formatMuscle(exercise.primary_muscle)}
                      </Badge>
                    )}
                    {exercise.secondary_muscles?.filter(m => !isRawValue(m)).map(m => (
                      <Badge key={m} variant="outline" className="rounded-lg text-sm capitalize px-3 py-1 font-heading border-white/15 text-white/60">
                        {formatMuscle(m)}
                      </Badge>
                    ))}
                  </div>

                  <Separator />

                  {/* Form & Technique - Focus on control, breathing, safety */}
                  {exercise.steps && exercise.steps.length > 0 && (
                    <div data-testid="exercise-steps">
                      <h3 className="font-heading font-black text-base uppercase tracking-tight mb-4 flex items-center gap-2">
                        <Dumbbell size={18} className="text-accent" />
                        Setup & Execution
                      </h3>
                      <ol className="space-y-3">
                        {exercise.steps.map((step, i) => (
                          <li key={i} className="flex gap-3 text-base">
                            <span className="shrink-0 w-7 h-7 rounded-lg bg-accent/15 text-accent flex items-center justify-center text-sm font-heading font-bold">
                              {i + 1}
                            </span>
                            <span className="text-white/70 leading-relaxed">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Do's and Don'ts - Focus on form and safety */}
                  {(exercise.coaching_cues || exercise.common_mistakes || exercise.dos || exercise.donts) && (
                    <>
                      <Separator />
                      <div className="space-y-4" data-testid="exercise-dos-donts">
                        {(exercise.coaching_cues || exercise.dos) && (exercise.coaching_cues?.length > 0 || exercise.dos?.length > 0) && (
                          <div>
                            <h3 className="font-heading font-black text-base uppercase tracking-tight mb-3 flex items-center gap-2 text-green-400">
                              <CheckCircle size={18} /> Coaching Cues
                            </h3>
                            <ul className="space-y-2.5">
                              {(exercise.coaching_cues || exercise.dos).slice(0, 5).map((item, i) => (
                                <li key={i} className="flex gap-2.5 text-base">
                                  <CheckCircle size={14} className="text-green-400 shrink-0 mt-1" />
                                  <span className="text-white/70">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(exercise.common_mistakes || exercise.donts) && (exercise.common_mistakes?.length > 0 || exercise.donts?.length > 0) && (
                          <div>
                            <h3 className="font-heading font-black text-base uppercase tracking-tight mb-3 flex items-center gap-2 text-red-400">
                              <XCircle size={18} /> Common Mistakes
                            </h3>
                            <ul className="space-y-2.5">
                              {(exercise.common_mistakes || exercise.donts).slice(0, 5).map((item, i) => (
                                <li key={i} className="flex gap-2.5 text-base">
                                  <XCircle size={14} className="text-red-400 shrink-0 mt-1" />
                                  <span className="text-white/70">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Safety Warnings */}
                  {exercise.safety_warnings && exercise.safety_warnings.length > 0 && (
                    <>
                      <Separator />
                      <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20" data-testid="exercise-safety">
                        <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2 text-orange-500">
                          <AlertTriangle size={16} /> Safety
                        </h3>
                        <ul className="space-y-1">
                          {exercise.safety_warnings.map((warn, i) => (
                            <li key={i} className="text-sm text-orange-200/80 flex gap-2">
                              <span className="text-orange-400">•</span>
                              {warn}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {/* Preference buttons — bottom of modal */}
                  <div className="pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <ExercisePreferenceButtons 
                      exercise={exercise}
                      workoutPlanId={workoutPlanId}
                      dayIndex={dayIndex}
                      exerciseIndex={exerciseIndex}
                      onExerciseReplaced={(data) => {
                        if (onExerciseReplaced) onExerciseReplaced(data);
                        onClose();
                      }}
                      onPreferenceUpdated={(update) => {
                        console.log('Preference updated:', update);
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
